import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Plane, Loader2, ClipboardList, Check, X, Car, Hotel, Train, Globe, Plus, Settings, Trash2, Edit2, Send, ListChecks, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import TravelChecklist from '../components/TravelChecklist';
import PersonalizedTravelChecklist from '../components/PersonalizedTravelChecklist';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = { flight: Plane, train: Train, bus: Train, cab: Car, hotel: Hotel, visa: Globe, forex: Globe };
const categoryLabels = { flight: 'Flight', train: 'Train', bus: 'Bus', cab: 'Cab', hotel: 'Hotel', visa: 'VISA', forex: 'Forex' };

const statusConfig = {
  pending_pilot: { label: 'Pending Pilot', color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  pending_admin: { label: 'Pending Admin', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  pending_l1: { label: 'Pending L1', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  pending_founder: { label: 'Pending Founder', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  pending_hr: { label: 'Pending HR', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const INDIAN_CITIES = [
  'Mumbai', 'Delhi', 'Bengaluru', 'Bangalore', 'Hyderabad', 'Ahmedabad', 'Chennai', 'Kolkata', 'Pune', 'Jaipur',
  'Surat', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore', 'Thane', 'Bhopal', 'Visakhapatnam', 'Patna', 'Vadodara',
  'Ghaziabad', 'Ludhiana', 'Agra', 'Nashik', 'Faridabad', 'Meerut', 'Rajkot', 'Kalyan', 'Vasai', 'Varanasi',
  'Srinagar', 'Aurangabad', 'Dhanbad', 'Amritsar', 'Navi Mumbai', 'Allahabad', 'Ranchi', 'Howrah', 'Coimbatore',
  'Jabalpur', 'Gwalior', 'Vijayawada', 'Jodhpur', 'Madurai', 'Raipur', 'Kota', 'Chandigarh', 'Guwahati', 'Solapur',
  'Hubli-Dharwad', 'Mysore', 'Tiruchirappalli', 'Bareilly', 'Aligarh', 'Tiruppur', 'Gurgaon', 'Gurugram', 'Noida',
  'Moradabad', 'Jalandhar', 'Bhubaneswar', 'Salem', 'Warangal', 'Guntur', 'Bhiwandi', 'Saharanpur', 'Gorakhpur',
  'Bikaner', 'Amravati', 'Jamshedpur', 'Bhilai', 'Cuttack', 'Firozabad', 'Kochi', 'Cochin', 'Nellore', 'Bhavnagar',
  'Dehradun', 'Durgapur', 'Asansol', 'Rourkela', 'Nanded', 'Kolhapur', 'Ajmer', 'Akola', 'Gulbarga', 'Jamnagar',
  'Ujjain', 'Loni', 'Siliguri', 'Jhansi', 'Ulhasnagar', 'Jammu', 'Sangli', 'Mangalore', 'Erode', 'Belgaum',
  'Kurnool', 'Rajahmundry', 'Bokaro', 'Kakinada', 'Nizamabad', 'Parbhani', 'Tumkur', 'Khammam', 'Rajapalayam',
  'Anantapur', 'Tirupati', 'Karnal', 'Bathinda', 'Rampur', 'Shimoga', 'Chandrapur', 'Junagadh', 'Thrissur', 'Alwar',
  'Bardhaman', 'Kulti', 'Kollam', 'Ratlam', 'Bilaspur', 'Shahjahanpur', 'Nadiad', 'Yamunanagar', 'Mahaboobnagar',
  'Sambalpur', 'Puri', 'Rewa', 'Gandhinagar', 'Panipat', 'Darbhanga', 'Bally', 'Muzaffarnagar', 'Muzaffarpur',
  'Bharatpur', 'Kharagpur', 'Mathura', 'Patiala',
  // Cities where Wisewit has offices
  'Bangalore (Wisewit HQ)', 'Hyderabad (Wisewit Ofc)',
];

const INTERNATIONAL_CITIES = [
  // Middle East / Gulf (heavy Wisewit-India traffic)
  'Dubai', 'Abu Dhabi', 'Sharjah', 'Doha', 'Muscat', 'Kuwait City', 'Riyadh', 'Jeddah', 'Manama',
  // South-East Asia
  'Singapore', 'Kuala Lumpur', 'Bangkok', 'Phuket', 'Jakarta', 'Bali (Denpasar)', 'Ho Chi Minh City', 'Hanoi', 'Manila',
  // East Asia
  'Hong Kong', 'Tokyo', 'Osaka', 'Seoul', 'Shanghai', 'Beijing', 'Taipei',
  // Europe
  'London', 'Manchester', 'Paris', 'Amsterdam', 'Frankfurt', 'Munich', 'Berlin', 'Zurich', 'Geneva', 'Rome', 'Milan',
  'Madrid', 'Barcelona', 'Lisbon', 'Vienna', 'Prague', 'Warsaw', 'Copenhagen', 'Stockholm', 'Helsinki', 'Oslo', 'Dublin', 'Brussels',
  // Americas
  'New York (JFK)', 'New York (EWR)', 'San Francisco', 'Los Angeles', 'Seattle', 'Chicago', 'Boston', 'Washington DC', 'Dallas', 'Houston', 'Atlanta', 'Miami',
  'Toronto', 'Vancouver', 'Montreal', 'Mexico City', 'São Paulo', 'Rio de Janeiro', 'Buenos Aires',
  // Oceania & Africa
  'Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Auckland', 'Wellington', 'Johannesburg', 'Cape Town', 'Nairobi', 'Cairo', 'Casablanca',
  // Other frequent India-outbound
  'Colombo', 'Male', 'Kathmandu', 'Dhaka',
];

const CITY_LIKE_KEYS = new Set(['origin', 'destination', 'city', 'pickup_location', 'drop_location']);

const CATEGORY_FIELDS = {
  flight: [
    { key: 'origin', label: 'Origin City', required: true },
    { key: 'destination', label: 'Destination City', required: true },
    { key: 'travel_date', label: 'Travel Date', type: 'date', required: true },
    { key: 'return_date', label: 'Return Date (if round trip)', type: 'date' },
    { key: 'preferred_time', label: 'Preferred Start Time', type: 'time' },
    { key: 'travelers', label: 'Number of Travelers', type: 'number' },
  ],
  train: [
    { key: 'origin', label: 'Origin Station', required: true },
    { key: 'destination', label: 'Destination Station', required: true },
    { key: 'travel_date', label: 'Travel Date', type: 'date', required: true },
    { key: 'preferred_class', label: 'Preferred Class (1AC/2AC/3AC/SL)' },
    { key: 'travelers', label: 'Number of Travelers', type: 'number' },
  ],
  bus: [
    { key: 'origin', label: 'Origin', required: true },
    { key: 'destination', label: 'Destination', required: true },
    { key: 'travel_date', label: 'Travel Date', type: 'date', required: true },
    { key: 'preferred_type', label: 'Type (AC/Non-AC/Sleeper)' },
  ],
  cab: [
    { key: 'pickup_location', label: 'Pickup Location', required: true },
    { key: 'drop_location', label: 'Drop Location', required: true },
    { key: 'travel_date', label: 'Date', type: 'date', required: true },
    { key: 'travel_time', label: 'Time', type: 'time', required: true },
    { key: 'purpose', label: 'Purpose (late-night drop / airport transfer / etc.)' },
  ],
  hotel: [
    { key: 'city', label: 'City', required: true },
    { key: 'check_in', label: 'Check-in Date', type: 'date', required: true },
    { key: 'check_out', label: 'Check-out Date', type: 'date', required: true },
    { key: 'budget', label: 'Budget Preference (per night)' },
  ],
  visa: [
    { key: 'destination_country', label: 'Destination Country', required: true },
    { key: 'travel_dates', label: 'Travel Dates', required: true },
    { key: 'passport_number', label: 'Passport Number', required: true },
  ],
  forex: [
    { key: 'currency', label: 'Currency Needed', required: true },
    { key: 'amount', label: 'Amount', type: 'number', required: true },
    { key: 'travel_dates', label: 'Travel Dates', required: true },
  ],
};

const TravelPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = ['admin', 'hr', 'manager'].includes(user?.role);
  const [activeTab, setActiveTab] = useState('book');
  const [requests, setRequests] = useState([]);
  const [pilotRequests, setPilotRequests] = useState([]);
  const [rules, setRules] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [personalizedFor, setPersonalizedFor] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Booking form state
  const [travelType, setTravelType] = useState('domestic');
  const [category, setCategory] = useState('');
  const [details, setDetails] = useState({});
  const [summary, setSummary] = useState('');
  const [autoAttendance, setAutoAttendance] = useState(true);
  const [destinationType, setDestinationType] = useState('client_site');

  // Rule editing
  const [editRule, setEditRule] = useState(null);
  const [newRule, setNewRule] = useState({ rule: '', category: 'general' });
  const [editSettings, setEditSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, pilotRes, rulesRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/travel/requests`, { withCredentials: true }),
        axios.get(`${API_URL}/api/travel/requests?view=pilot`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/travel/rules`, { withCredentials: true }),
        axios.get(`${API_URL}/api/travel/settings`, { withCredentials: true }),
      ]);
      setRequests(reqRes.data);
      setPilotRequests(pilotRes.data);
      setRules(rulesRes.data);
      setSettings(settingsRes.data);
      setSettingsForm(settingsRes.data);
    } catch (err) {
      console.error('Failed to fetch travel data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!category) { toast.error('Please select a travel category'); return; }
    const fields = CATEGORY_FIELDS[category] || [];
    for (const f of fields) {
      if (f.required && !details[f.key]) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const autoSummary = summary || `${travelType} ${categoryLabels[category] || category} - ${details.origin || details.city || details.pickup_location || details.destination_country || ''} ${details.destination ? '→ ' + details.destination : ''} ${details.drop_location ? '→ ' + details.drop_location : ''}`.trim();
      await axios.post(`${API_URL}/api/travel/request`, {
        travel_type: travelType,
        category,
        details,
        summary: autoSummary,
        auto_attendance: autoAttendance,
        destination_type: destinationType,
      }, { withCredentials: true });
      toast.success('Travel request submitted! Admin has been notified via email.');
      setCategory('');
      setDetails({});
      setSummary('');
      setAutoAttendance(true);
      setDestinationType('client_site');
      setActiveTab('requests');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!reviewDialog) return;
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/travel/requests/${reviewDialog.id}/approve`, { remarks: reviewRemarks }, { withCredentials: true });
      toast.success('Travel request approved');
      setReviewDialog(null);
      setReviewRemarks('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!reviewDialog) return;
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/travel/requests/${reviewDialog.id}/reject`, { remarks: reviewRemarks }, { withCredentials: true });
      toast.success('Travel request rejected');
      setReviewDialog(null);
      setReviewRemarks('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddRule = async () => {
    if (!newRule.rule.trim()) return;
    try {
      await axios.post(`${API_URL}/api/travel/rules`, newRule, { withCredentials: true });
      toast.success('Rule added');
      setNewRule({ rule: '', category: 'general' });
      fetchData();
    } catch (err) {
      toast.error('Failed to add rule');
    }
  };

  const handleUpdateRule = async (ruleId, data) => {
    try {
      await axios.put(`${API_URL}/api/travel/rules/${ruleId}`, data, { withCredentials: true });
      toast.success('Rule updated');
      setEditRule(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API_URL}/api/travel/rules/${ruleId}`, { withCredentials: true });
      toast.success('Rule deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API_URL}/api/travel/settings`, settingsForm, { withCredentials: true });
      toast.success('Settings saved');
      setEditSettings(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const canApprove = (req) => {
    return isManager && !['approved', 'rejected'].includes(req.status);
  };

  const myRequests = requests.filter(r => r.employee_id === user?.id);
  const pendingForMe = requests.filter(r => canApprove(r));
  const pilotPending = pilotRequests.filter(r => r.status === 'pending_pilot');
  const fields = CATEGORY_FIELDS[category] || [];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="travel-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Plane className="w-7 h-7 text-primary" />
          Travel
        </h1>
        <p className="text-muted-foreground">Book travel and manage requests</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="travel-tabs">
          <TabsTrigger value="book" data-testid="tab-travel-book"><Send className="w-4 h-4 mr-1.5" />Book Travel</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-travel-requests">
            <ClipboardList className="w-4 h-4 mr-1.5" />My Requests
            {myRequests.filter(r => !['approved', 'rejected'].includes(r.status)).length > 0 && (
              <span className="ml-1.5 w-5 h-5 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">
                {myRequests.filter(r => !['approved', 'rejected'].includes(r.status)).length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-travel-rules"><ClipboardList className="w-4 h-4 mr-1.5" />Travel Policy</TabsTrigger>
          <TabsTrigger value="checklist" data-testid="tab-travel-checklist"><ListChecks className="w-4 h-4 mr-1.5" />Preparation Checklist</TabsTrigger>
          {pilotPending.length > 0 && (
            <TabsTrigger value="pilot" data-testid="tab-travel-pilot">
              <Check className="w-4 h-4 mr-1.5" />Pilot Approvals
              <span className="ml-1.5 w-5 h-5 rounded-full bg-violet-500 text-white text-xs flex items-center justify-center">
                {pilotPending.length}
              </span>
            </TabsTrigger>
          )}
          {isManager && (
            <TabsTrigger value="approvals" data-testid="tab-travel-approvals">
              <Check className="w-4 h-4 mr-1.5" />Approvals
              {pendingForMe.length > 0 && (
                <span className="ml-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  {pendingForMe.length}
                </span>
              )}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="settings" data-testid="tab-travel-settings"><Settings className="w-4 h-4 mr-1.5" />Settings</TabsTrigger>
          )}
        </TabsList>

        {/* Book Travel Tab */}
        <TabsContent value="book" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Booking Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">New Travel Request</CardTitle>
                  <CardDescription>Fill in the details below. Admin will be notified automatically.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitRequest} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Travel Type</Label>
                        <Select value={travelType} onValueChange={setTravelType}>
                          <SelectTrigger data-testid="travel-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="domestic">Domestic</SelectItem>
                            <SelectItem value="international">International</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select value={category} onValueChange={(v) => { setCategory(v); setDetails({}); }}>
                          <SelectTrigger data-testid="travel-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            {(travelType === 'domestic'
                              ? ['flight', 'train', 'bus', 'cab', 'hotel']
                              : ['flight', 'hotel', 'visa', 'forex']
                            ).map(c => (
                              <SelectItem key={c} value={c}>{categoryLabels[c]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {category && fields.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        {fields.map(f => {
                          const isCity = CITY_LIKE_KEYS.has(f.key);
                          const listId = isCity ? `city-suggest-${f.key}` : undefined;
                          return (
                            <div key={f.key} className="space-y-2">
                              <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
                              <Input
                                type={f.type || 'text'}
                                value={details[f.key] || ''}
                                onChange={(e) => setDetails(prev => ({ ...prev, [f.key]: e.target.value }))}
                                required={f.required}
                                list={listId}
                                autoComplete={isCity ? 'off' : undefined}
                                data-testid={`travel-field-${f.key}`}
                                placeholder={isCity ? 'Start typing city name…' : (f.type === 'time' ? 'HH:MM' : '')}
                              />
                              {isCity && (
                                <datalist id={listId}>
                                  {(travelType === 'international'
                                    ? [...INTERNATIONAL_CITIES, ...INDIAN_CITIES]
                                    : INDIAN_CITIES
                                  ).map(c => (<option key={c} value={c} />))}
                                </datalist>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label>Summary / Additional Notes</Label>
                      <Textarea
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        placeholder="Any additional details or preferences..."
                        rows={2}
                        data-testid="travel-summary"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border bg-muted/30 p-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Destination type</Label>
                        <Select value={destinationType} onValueChange={setDestinationType}>
                          <SelectTrigger data-testid="destination-type-select" className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="client_site">Client site</SelectItem>
                            <SelectItem value="conference">Conference / event</SelectItem>
                            <SelectItem value="other_office_branch">Another Wisewit office branch</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Auto-mark attendance</Label>
                        <label className="flex items-center gap-2 text-sm h-9">
                          <input
                            type="checkbox"
                            checked={autoAttendance && destinationType !== 'other_office_branch'}
                            disabled={destinationType === 'other_office_branch'}
                            onChange={e => setAutoAttendance(e.target.checked)}
                            className="w-4 h-4 accent-primary"
                            data-testid="auto-attendance-checkbox"
                          />
                          <span className="text-xs text-muted-foreground">
                            {destinationType === 'other_office_branch'
                              ? 'Skipped for other office branches — mark manually there.'
                              : 'Mark attendance automatically once approved'}
                          </span>
                        </label>
                      </div>
                    </div>

                    <Button type="submit" disabled={submitting || !category} className="w-full gap-2" data-testid="submit-travel-request">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      Submit Travel Request
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Policy Sidebar */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Travel Policy</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {rules.filter(r => r.active).map((rule) => (
                    <div key={rule.id} className="flex items-start gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                      <p className="text-muted-foreground">{rule.rule}</p>
                    </div>
                  ))}
                  {settings.per_diem_rate > 0 && (
                    <div className="flex items-start gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 shrink-0" />
                      <p className="text-muted-foreground">Per-diem allowance: <strong>INR {settings.per_diem_rate}/day</strong></p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* My Requests Tab */}
        <TabsContent value="requests" className="mt-4">
          {myRequests.length === 0 ? (
            <Card><CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <Plane className="w-12 h-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No travel requests yet</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {myRequests.map((req) => {
                const CatIcon = categoryIcons[req.category] || Plane;
                const sCfg = statusConfig[req.status] || statusConfig.pending_admin;
                return (
                  <Card key={req.id} data-testid={`my-travel-${req.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="p-2.5 rounded-lg bg-primary/10 text-primary"><CatIcon className="w-5 h-5" /></div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-sm">{req.summary}</p>
                              <Badge variant="outline" className="text-xs capitalize">{req.travel_type}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {new Date(req.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} — {req.category}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {req.status === 'approved' && (
                            <Button
                              size="sm" variant="outline" className="gap-1.5 text-xs"
                              onClick={() => setPersonalizedFor(req.id)}
                              data-testid={`view-checklist-${req.id}`}
                            >
                              <Sparkles className="w-3.5 h-3.5" /> Pack List
                            </Button>
                          )}
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${sCfg.color}`}>{sCfg.label}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Travel Policy Tab */}
        <TabsContent value="rules" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Travel Booking Rules</CardTitle>
              <CardDescription>These rules guide travel bookings at Wisewit</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {rules.filter(r => r.active).map((rule, i) => (
                <div key={rule.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                  <span className="text-sm font-semibold text-primary mt-0.5">{i + 1}.</span>
                  <div className="flex-1">
                    <p className="text-sm">{rule.rule}</p>
                    <Badge variant="outline" className="text-xs mt-1 capitalize">{rule.category}</Badge>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditRule(rule)} data-testid={`edit-rule-${rule.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => handleDeleteRule(rule.id)} data-testid={`delete-rule-${rule.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              {isAdmin && (
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm font-medium mb-2">Add New Rule</p>
                  <div className="flex gap-2">
                    <Input value={newRule.rule} onChange={(e) => setNewRule(prev => ({ ...prev, rule: e.target.value }))} placeholder="Enter new travel rule..." className="flex-1" data-testid="new-rule-input" />
                    <Select value={newRule.category} onValueChange={(v) => setNewRule(prev => ({ ...prev, category: v }))}>
                      <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['general', 'flight', 'train', 'bus', 'cab', 'hotel', 'international'].map(c => (
                          <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddRule} disabled={!newRule.rule.trim()} className="gap-1" data-testid="add-rule-btn">
                      <Plus className="w-4 h-4" /> Add
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Travel Preparation Checklist Tab */}
        <TabsContent value="checklist" className="mt-4">
          <TravelChecklist isAdmin={isAdmin} />
        </TabsContent>

        {/* Pilot Approvals Tab */}
        {pilotPending.length > 0 && (
          <TabsContent value="pilot" className="mt-4">
            <div className="space-y-3">
              {pilotRequests.map((req) => {
                const CatIcon = categoryIcons[req.category] || Plane;
                const sCfg = statusConfig[req.status] || statusConfig.pending_pilot;
                const showActions = req.status === 'pending_pilot';
                return (
                  <Card key={req.id} className={showActions ? 'border-violet-300/50' : ''} data-testid={`pilot-travel-${req.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="p-2.5 rounded-lg bg-violet-500/10 text-violet-600"><CatIcon className="w-5 h-5" /></div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm">{req.summary}</p>
                            <p className="text-xs text-muted-foreground"><span className="font-medium">{req.employee_name}</span> — {req.travel_type} / {req.category}</p>
                            {Object.entries(req.details || {}).slice(0, 3).map(([k, v]) => (
                              <p key={k} className="text-xs text-muted-foreground capitalize">{k.replace(/_/g, ' ')}: {v}</p>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${sCfg.color}`}>{sCfg.label}</span>
                          {showActions && (
                            <Button size="sm" onClick={() => { setReviewDialog(req); setReviewRemarks(''); }} data-testid={`pilot-review-${req.id}`}>Review</Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        )}

        {/* Approvals Tab */}
        {isManager && (
          <TabsContent value="approvals" className="mt-4">
            {requests.length === 0 ? (
              <Card><CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Check className="w-12 h-12 text-muted-foreground/30 mb-3" />
                <p className="text-muted-foreground">No travel requests to review</p>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {requests.map((req) => {
                  const CatIcon = categoryIcons[req.category] || Plane;
                  const sCfg = statusConfig[req.status] || statusConfig.pending_admin;
                  const showApprove = canApprove(req);
                  return (
                    <Card key={req.id} className={showApprove ? 'border-amber-300/50' : ''} data-testid={`approve-travel-${req.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2.5 rounded-lg bg-primary/10 text-primary"><CatIcon className="w-5 h-5" /></div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm">{req.summary}</p>
                              <p className="text-xs text-muted-foreground"><span className="font-medium">{req.employee_name}</span> — {req.travel_type} / {req.category}</p>
                              {Object.entries(req.details || {}).slice(0, 3).map(([k, v]) => (
                                <p key={k} className="text-xs text-muted-foreground capitalize">{k.replace(/_/g, ' ')}: {v}</p>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${sCfg.color}`}>{sCfg.label}</span>
                            {showApprove && (
                              <Button size="sm" onClick={() => { setReviewDialog(req); setReviewRemarks(''); }} data-testid={`review-travel-${req.id}`}>Review</Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        )}

        {/* Settings Tab (Admin) */}
        {isAdmin && (
          <TabsContent value="settings" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Travel Settings</CardTitle>
                <CardDescription>Configure travel notification email and per-diem rate</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Admin Notification Email</Label>
                    <Input value={settingsForm.admin_email || ''} onChange={(e) => setSettingsForm(prev => ({ ...prev, admin_email: e.target.value }))} placeholder="contact@wisewit.ai" data-testid="settings-admin-email" />
                    <p className="text-xs text-muted-foreground">Travel requests will be emailed to this address</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Per-diem Rate (INR/day)</Label>
                    <Input type="number" value={settingsForm.per_diem_rate || ''} onChange={(e) => setSettingsForm(prev => ({ ...prev, per_diem_rate: parseInt(e.target.value) || 0 }))} placeholder="200" data-testid="settings-per-diem" />
                    <p className="text-xs text-muted-foreground">Added to salary at month end</p>
                  </div>
                </div>
                <Button onClick={handleSaveSettings} data-testid="save-travel-settings">Save Settings</Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={(open) => !open && setReviewDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Travel Request</DialogTitle>
            <DialogDescription>Approve or reject this travel request.</DialogDescription>
          </DialogHeader>
          {reviewDialog && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-muted-foreground text-xs">Employee</p><p className="font-medium">{reviewDialog.employee_name}</p></div>
                <div><p className="text-muted-foreground text-xs">Type</p><p className="font-medium capitalize">{reviewDialog.travel_type} — {reviewDialog.category}</p></div>
                <div className="col-span-2"><p className="text-muted-foreground text-xs">Summary</p><p className="font-medium">{reviewDialog.summary}</p></div>
              </div>
              {reviewDialog.details && Object.keys(reviewDialog.details).length > 0 && (
                <div className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Booking Details</p>
                  {Object.entries(reviewDialog.details).map(([k, v]) => (
                    <p key={k} className="text-sm capitalize"><span className="text-muted-foreground">{k.replace(/_/g, ' ')}:</span> {v}</p>
                  ))}
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Remarks</Label>
                <Textarea value={reviewRemarks} onChange={(e) => setReviewRemarks(e.target.value)} placeholder="Add remarks..." rows={2} data-testid="travel-review-remarks" />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReviewDialog(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading} className="gap-1.5" data-testid="reject-travel-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Reject
            </Button>
            <Button onClick={handleApprove} disabled={actionLoading} className="gap-1.5" data-testid="approve-travel-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Approve
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Rule Dialog */}
      <Dialog open={!!editRule} onOpenChange={(open) => !open && setEditRule(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Travel Rule</DialogTitle></DialogHeader>
          {editRule && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Rule</Label>
                <Textarea value={editRule.rule} onChange={(e) => setEditRule(prev => ({ ...prev, rule: e.target.value }))} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={editRule.category} onValueChange={(v) => setEditRule(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['general', 'flight', 'train', 'bus', 'cab', 'hotel', 'international'].map(c => (
                      <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditRule(null)}>Cancel</Button>
                <Button onClick={() => handleUpdateRule(editRule.id, { rule: editRule.rule, category: editRule.category })}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Personalized Travel Checklist Modal */}
      <PersonalizedTravelChecklist
        requestId={personalizedFor}
        open={!!personalizedFor}
        onOpenChange={(o) => { if (!o) setPersonalizedFor(null); }}
        canSendReminder={isAdmin}
      />
    </div>
  );
};

export default TravelPage;
